import { css } from "@linaria/core";
import cx from "classnames";
import React, { memo, useState } from "react";
import { ArrowDropRightLine } from "@ui/IconV2/react";
import Popper from "@ui/Popper";
import Text from "@ui/Text";
import { colors, shadows } from "@ui/Theme";

export type ContextualMenuItem = {
  id: string;
  name: React.ReactNode;
  value?: unknown;
  enabled?: boolean;
  items: ContextualMenuItem[];
};

const ContextualMenuContainer = css`
  background-color: ${colors.hydrogen};
  color: ${colors.carbon900};
  width: 100%;
  max-width: 350px;
  min-width: 275px;
`;

const ContextualMenuSubContainer = css`
  box-shadow: ${shadows.shadow100};
  border-radius: 4px;
  max-height: 50vh;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 8px;
`;

const Item = css`
  align-items: center;
  background-color: transparent;
  border: none;
  display: flex;
  width: 100%;
  user-select: none;
  border-radius: 4px;
  overflow: hidden;

  &:hover,
  &.active {
    background-color: ${colors.titanium100};
  }
`;

const ItemActive = css`
  background-color: ${colors.titanium100};
`;

const ItemDisabled = css`
  cursor: not-allowed;
  opacity: 0.5;
`;

const ItemText = css`
  align-items: center;
  display: flex;
  gap: 8px;
  padding: 4px 8px;
  max-width: calc(100% - 24px);
  white-space: nowrap;
`;

const ItemButton = css`
  background-color: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 100%;

  &:disabled {
    appearance: none;
    cursor: not-allowed;
  }
`;

const ItemButtonDisabled = css`
  pointer-events: none;
`;

const ItemArrow = css`
  margin-left: auto;
  flex-shrink: 0;
`;

export const ContextualMenu = memo(
  ({
    items,
    onClick,
    contextualSubMenu = false,
  }: {
    items: ContextualMenuItem[];
    contextualSubMenu?: boolean;
    onClick: (value: unknown) => void;
  }) => {
    const [active, setActive] = useState<string | null>(null);
    return (
      <ul
        className={cx(ContextualMenuContainer, { [ContextualMenuSubContainer]: contextualSubMenu })}
        onMouseLeave={() => setActive(null)}
      >
        {items.map(item => (
          <Popper
            key={item.id}
            hover={item.enabled && item.items.length > 0}
            keepOpenOnOverlayActive
            placement="left-start"
            backgroundColor="transparent"
            overlay={<ContextualMenu contextualSubMenu items={item.items} onClick={onClick} />}
          >
            <li
              className={cx(Item, {
                [ItemActive]: active === item.id,
                [ItemDisabled]: !item.enabled,
              })}
              onMouseEnter={() => setActive(item.id)}
            >
              {item.items.length > 0 ? (
                <Text className={ItemText} type="body1">
                  {item.name}
                </Text>
              ) : (
                <button
                  className={cx(ItemButton, { [ItemButtonDisabled]: !item.enabled })}
                  onClick={() => onClick(item.value)}
                >
                  <Text className={ItemText} type="body1">
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "inline-block",
                        overflowWrap: "normal",
                      }}
                    >
                      {item.name}
                    </span>
                  </Text>
                </button>
              )}
              {item.items.length > 0 && (
                <ArrowDropRightLine size="l" color={colors.raw.carbon700} className={ItemArrow} />
              )}
            </li>
          </Popper>
        ))}
      </ul>
    );
  },
);
